import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, MessageSquare, Wallet } from "lucide-react";
import FileDropzone, { resolveFileUrl } from "@/components/FileDropzone";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: "", bio: "", avatar_url: "", cover_url: "", city: "", lightning_address: "" });
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [feeInfo, setFeeInfo] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || "",
        cover_url: user.cover_url || "",
        city: user.city || "",
        lightning_address: user.lightning_address || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (user?.paid_entry) {
      api.get("/bitdate/matches").then((r) => setMatches(r.data)).catch(() => {});
      api.get("/marketplace/my-fees").then((r) => setFeeInfo(r.data)).catch(() => {});
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refreshUser();
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      <div className="relative border-2 border-black h-64 bg-gradient-to-br from-[#F7931A] to-[#0A0A0A] overflow-hidden">
        {form.cover_url && (
          <img src={resolveFileUrl(form.cover_url)} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute -bottom-12 left-8 w-28 h-28 border-4 border-[#FDFBF7] bg-white overflow-hidden">
          {form.avatar_url ? (
            <img src={resolveFileUrl(form.avatar_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display font-black text-5xl text-[#F7931A]">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
        </div>
      </div>

      <div className="mt-16 px-2 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{user.name}</h1>
          <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 mt-1">
            {user.email} · {user.paid_entry ? "Verified member" : "Payment pending"}
          </div>
        </div>
      </div>

      <form onSubmit={save} data-testid="profile-form" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Avatar</div>
            <FileDropzone
              value={form.avatar_url}
              onChange={(u) => setForm({ ...form, avatar_url: u })}
              accept="image/*"
              label="Upload avatar"
              testId="profile-avatar-upload"
              aspect="aspect-square max-w-[180px]"
            />
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Cover photo</div>
            <FileDropzone
              value={form.cover_url}
              onChange={(u) => setForm({ ...form, cover_url: u })}
              accept="image/*"
              label="Upload cover"
              testId="profile-cover-upload"
              aspect="aspect-[16/6]"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest">Display name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="profile-name"
            className="mt-2 w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest">City</label>
          <input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="profile-city"
            className="mt-2 w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
            Lightning Address
            <span className="font-mono text-[10px] normal-case tracking-normal text-[#0A0A0A]/50">
              (e.g. you@walletofsatoshi.com) — required to accept marketplace payments
            </span>
          </label>
          <input
            type="email"
            value={form.lightning_address}
            onChange={(e) => setForm({ ...form, lightning_address: e.target.value })}
            data-testid="profile-lightning"
            placeholder="satoshi@getalby.com"
            className="mt-2 w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-bold uppercase tracking-widest">Bio</label>
          <textarea
            value={form.bio}
            rows={3}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            data-testid="profile-bio"
            className="mt-2 w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm resize-none"
          />
        </div>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save"
            className="px-6 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal disabled:opacity-50"
          >
            {saving ? "..." : "Save profile"}
          </button>
        </div>
      </form>

      {feeInfo && feeInfo.total_owed_btc > 0 && (
        <section
          data-testid="platform-fees-panel"
          className="mt-14 border-2 border-black bg-[#0A0A0A] text-[#FDFBF7] p-6"
        >
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#F7931A]" />
            <div className="font-display font-black uppercase text-2xl">Marketplace fees owed</div>
          </div>
          <div className="mt-4 font-mono text-3xl font-bold text-[#F7931A]">
            {feeInfo.total_owed_btc.toFixed(8)} BTC
          </div>
          <div className="mt-1 font-mono text-xs uppercase tracking-widest text-white/60">
            {feeInfo.fee_pct}% platform fee on {feeInfo.sales.length} completed sale(s)
          </div>
          <div className="mt-4 text-sm text-white/70">
            Send this amount to the platform address shown on the Donate page to clear your balance.
          </div>
        </section>
      )}

      {user.paid_entry && (
        <section className="mt-14">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#F7931A]" />
            <h2 className="font-display font-black text-2xl uppercase tracking-tighter">Your matches</h2>
          </div>
          {matches.length === 0 ? (
            <div className="text-sm font-mono text-[#0A0A0A]/60">No matches yet. Try BitDate.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {matches.map((m) => (
                <div
                  key={m.match_id}
                  data-testid={`match-${m.match_id}`}
                  className="border-2 border-black bg-white p-4"
                >
                  <div className="w-full aspect-square border border-black overflow-hidden bg-[#F7931A]/10 mb-3">
                    {m.user.avatar_url ? (
                      <img src={resolveFileUrl(m.user.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display font-black text-4xl text-[#F7931A]">
                        {m.user.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="font-display font-bold">{m.user.name}</div>
                  <div className="font-mono text-xs uppercase text-[#0A0A0A]/60">{m.user.city || "—"}</div>
                  <Link
                    to={`/messages?to=${m.user.id}`}
                    data-testid={`match-msg-${m.match_id}`}
                    className="mt-3 flex items-center justify-center gap-2 py-2 bg-[#F7931A] border-2 border-black text-xs font-bold uppercase tracking-widest btn-brutal"
                  >
                    <MessageSquare className="w-3 h-3" />
                    Message
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
